/* eslint-disable @atlaskit/platform/one-value-export-per-file -- Keep the full identity reader beside the account-ID fetch so both use its private cache. */
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import {
	performGqlFetch,
	type GqlSuccessResponse,
} from '@atlassian/conversation-assistant-utils/graphqlUtils';

type RovoChatAgentIdentity = {
	identityAccountId?: string | null;
	name?: string | null;
	description?: string | null;
	identityAccount?: { picture?: string | null } | null;
};

type RovoChatAgentResponse = {
	agentStudio_getAgents?: {
		edges?: Array<{
			node?: RovoChatAgentIdentity;
		}>;
	};
};

type RovoChatAgentVariables = {
	cloudId: string;
	product: string;
	includeMentionFields: boolean;
};

const rovoChatAgentIdentityRequests = new Map<
	string,
	Promise<GqlSuccessResponse<RovoChatAgentResponse>>
>();

const getIdentityAccountId = async ({
	cacheKey,
	request,
}: {
	cacheKey: string;
	request: Promise<GqlSuccessResponse<RovoChatAgentResponse>>;
}): Promise<string | undefined> => {
	try {
		const response = await request;
		return response.data.agentStudio_getAgents?.edges?.[0]?.node?.identityAccountId ?? undefined;
	} catch {
		if (rovoChatAgentIdentityRequests.get(cacheKey) === request) {
			rovoChatAgentIdentityRequests.delete(cacheKey);
		}
		return undefined;
	}
};

export const fetchRovoChatAgentIdentityAccountId = async ({
	cloudId,
	product,
}: {
	cloudId: string;
	product: string;
}): Promise<string | undefined> => {
	const cacheKey = `${cloudId}:${product}`;
	const cachedRequest = rovoChatAgentIdentityRequests.get(cacheKey);
	if (cachedRequest) {
		return getIdentityAccountId({ cacheKey, request: cachedRequest });
	}

	const request = performGqlFetch<RovoChatAgentResponse, RovoChatAgentVariables>({
		gqlQuery: `
			query fetchRovoChatAgentIdentity($cloudId: String!, $product: String, $includeMentionFields: Boolean!) {
				agentStudio_getAgents(
					cloudId: $cloudId
					first: 1
					input: { creatorTypes: [ROVO_CHAT] }
					product: $product
				) @optIn(to: "AgentStudio") {
					edges {
						node {
							identityAccountId
							name @include(if: $includeMentionFields)
							description @include(if: $includeMentionFields)
							identityAccount @include(if: $includeMentionFields) { picture }
						}
					}
				}
			}
		`,
		variables: {
			cloudId,
			product,
			includeMentionFields: isExperimentEnabled('platform_editor_mention_rovo'),
		},
	});

	rovoChatAgentIdentityRequests.set(cacheKey, request);
	return getIdentityAccountId({ cacheKey, request });
};

/** Reuses the existing cached identity request, fetching on a cache miss. */
export const fetchRovoChatAgentIdentity = async (params: {
	cloudId: string;
	product: string;
}): Promise<RovoChatAgentIdentity | undefined> => {
	await fetchRovoChatAgentIdentityAccountId(params);
	return rovoChatAgentIdentityRequests.get(`${params.cloudId}:${params.product}`)?.then(
		(response) => response.data.agentStudio_getAgents?.edges?.[0]?.node ?? undefined,
		() => undefined,
	);
};
